/**
 * Gráfico Stacked Bar Horizontal para Processos por Tipo x Status
 * Baseado no processosStatusChart.js do backup
 */
import { useMemo, useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { cn } from '@/lib/utils';
import type { TypeStatusRow } from '../hooks';

// Hook para detectar dark mode
function useIsDarkMode() {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

interface ProcessosStatusChartProps {
  data: TypeStatusRow[];
  statuses: string[];
  isLoading?: boolean;
  className?: string;
}

// Cores para cada status de processo
const STATUS_COLORS: Record<string, string> = {
  'Em andamento': '#3b82f6', // blue-500
  'Atrasado': '#ef4444', // red-500
  'Concluído': '#10b981', // emerald-500
  'Cancelado': '#6b7280', // gray-500
  'Pausado': '#f59e0b', // amber-500
  'Aguardando aprovação': '#8b5cf6', // violet-500
  'Aguardando documentação': '#f97316', // orange-500
  'Outros': '#9ca3af', // gray-400
};

function getStatusColor(status: string): string {
  // Busca case-insensitive
  const key = Object.keys(STATUS_COLORS).find(
    (k) => k.toLowerCase() === status.toLowerCase()
  );
  return key ? STATUS_COLORS[key] : STATUS_COLORS['Outros'];
}

export function ProcessosStatusChart({
  data,
  statuses,
  isLoading = false,
  className,
}: ProcessosStatusChartProps) {
  const isDark = useIsDarkMode();
  const textColor = isDark ? '#e5e7eb' : '#374151';
  
  const option: EChartsOption = useMemo(() => {
    if (!data || data.length === 0) return {};

    const categories = data.map((r) => r.docType);

    // Construir série para cada status (stack)
    const series = statuses.map((status) => ({
      name: status,
      type: 'bar' as const,
      stack: 'total',
      emphasis: { focus: 'series' as const },
      label: { show: false },
      itemStyle: { color: getStatusColor(status) },
      data: data.map((r) => {
        const found = r.statuses.find((s) => s.status === status);
        return found ? found.count : 0;
      }),
    }));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const paramsArray = params as Array<{
            name: string;
            marker: string;
            seriesName: string;
            value: number;
          }>;
          if (!paramsArray.length) return '';
          const total = paramsArray.reduce((s, p) => s + p.value, 0);
          const lines = paramsArray
            .filter((p) => p.value > 0)
            .map(
              (p) =>
                `${p.marker} ${p.seriesName}: <strong>${p.value.toLocaleString('pt-BR')}</strong>`
            );
          return `<strong>${paramsArray[0].name}</strong><br/>${lines.join('<br/>')}<br/>Total: <strong>${total.toLocaleString('pt-BR')}</strong>`;
        },
      },
      legend: {
        top: 0,
        type: 'scroll',
        textStyle: { fontSize: 11, color: textColor },
      },
      grid: {
        left: 24,
        right: 12,
        top: 50,
        bottom: 12,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          color: textColor,
          formatter: (value: number) => value.toLocaleString('pt-BR'),
        },
        splitLine: {
          lineStyle: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' },
        },
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          color: textColor,
          width: 120,
          overflow: 'truncate',
          ellipsis: '...',
        },
      },
      series,
    };
  }, [data, statuses, isDark, textColor]);

  if (isLoading) {
    return (
      <div className={cn('bg-card rounded-xl p-4 shadow-sm', className)}>
        <div className="flex items-center justify-center h-[300px]">
          <div className="animate-pulse text-muted-foreground">Carregando...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={cn('bg-card rounded-xl p-4 shadow-sm', className)}>
        <h3 className="text-sm font-medium text-muted-foreground mb-2">
          Processos por Tipo x Status
        </h3>
        <div className="flex items-center justify-center h-[300px] text-muted-foreground">
          Sem dados disponíveis
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-card rounded-xl p-4 shadow-sm', className)}>
      <h3 className="text-sm font-medium text-muted-foreground mb-2">
        Processos por Tipo x Status
      </h3>
      <ReactECharts
        option={option}
        style={{ height: '300px', width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
}
